// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md

import * as React from 'react';

import { KeyRound } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { IDP_PROVIDERS, type IdpProviderId } from '@/lib/idpProviders';
import type { RoleSource } from '@greenlight/shared';

import { GoogleClientFileImport } from './GoogleClientFileImport';
import type { IdpFormState } from './useIdpForm';

/**
 * The OIDC-provider editor fields shared by the install wizard (`IdpStep`) and
 * Settings → Identity (`IdentitySettings`). Rendered from `useIdpForm` state so
 * both surfaces present one source of truth for the inputs, validation gating,
 * and role-mapping copy. Each surface owns the surrounding shell — header,
 * warnings, the validation-probe display, and the action buttons.
 */

export interface IdpProviderPickerProps {
  providerId: IdpProviderId | null;
  onSelect: (id: IdpProviderId) => void;
  legend: string;
}

export function IdpProviderPicker({
  providerId,
  onSelect,
  legend,
}: IdpProviderPickerProps): React.JSX.Element {
  return (
    <fieldset className="space-y-3" data-testid="idp-providers">
      <legend className="text-sm font-medium">{legend}</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        {IDP_PROVIDERS.map((p) => {
          const selected = p.id === providerId;
          return (
            <button
              key={p.id}
              type="button"
              disabled={!p.available}
              aria-pressed={selected}
              onClick={() => onSelect(p.id)}
              data-testid={`idp-option-${p.id}`}
              className={cn(
                'flex items-start gap-3 rounded-lg border p-4 text-left transition-colors min-h-11',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                selected && 'border-primary ring-1 ring-primary',
                p.available ? 'hover:border-primary/60' : 'cursor-not-allowed opacity-60',
              )}
            >
              <span
                aria-hidden
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted"
              >
                {p.mark ? (
                  <img src={p.mark} alt="" width={22} height={22} />
                ) : (
                  <KeyRound className="h-5 w-5" />
                )}
              </span>
              <span className="min-w-0">
                <span className="block font-medium">
                  {p.label}
                  {!p.available && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      Coming soon
                    </span>
                  )}
                </span>
                <span className="block text-sm text-muted-foreground">{p.blurb}</span>
              </span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export interface IdpProviderFieldsProps {
  form: IdpFormState;
  /** The install's OIDC callback URL; Google's client-file import checks it. */
  redirectUri: string;
  /** Greenlight-managed role-source card blurb — copy differs per surface. */
  greenlightBlurb: string;
  /** Note shown for Google, whose roles are dashboard-managed. */
  googleRolesNote: React.ReactNode;
  /** Rendered below the Workspace-domains field (the wizard's lockout warning). */
  hostedDomainsExtra?: React.ReactNode;
  /** Rendered below the role-source cards (Settings' mode-change warnings). */
  roleSourceExtra?: React.ReactNode;
  /** Rendered when Greenlight-managed (the wizard's initial-admin email field). */
  greenlightExtra?: React.ReactNode;
  /** Replaces the client-secret hint (the wizard's note on a saved secret). */
  secretHint?: string;
}

export function IdpProviderFields({
  form,
  redirectUri,
  greenlightBlurb,
  googleRolesNote,
  hostedDomainsExtra,
  roleSourceExtra,
  greenlightExtra,
  secretHint,
}: IdpProviderFieldsProps): React.JSX.Element {
  const opensInFullIssuer = (): boolean => {
    const tenant = form.providerDef?.tenantIssuer;
    return (
      tenant !== undefined &&
      form.issuerUrl.trim() !== '' &&
      tenant.fromIssuer(form.issuerUrl) === null
    );
  };
  // Decided when the form opens or the provider changes, then only by the
  // links, so editing the full issuer never swaps the input out from under it.
  const [fullIssuerChosen, setFullIssuerChosen] = React.useState(opensInFullIssuer);
  React.useEffect(() => setFullIssuerChosen(opensInFullIssuer()), [form.provider]);
  const def = form.providerDef;
  if (!def) return <></>;

  const tenantIssuer = def.tenantIssuer;
  const storedTenant = tenantIssuer?.fromIssuer(form.issuerUrl) ?? null;
  const showTenant = tenantIssuer !== undefined && !fullIssuerChosen;

  const onTenantChange = (value: string): void => {
    if (!tenantIssuer) return;
    if (!/^https?:\/\//i.test(value.trim())) {
      form.setIssuerUrl(tenantIssuer.toIssuer(value.replace(/[\s/]/g, '')));
      return;
    }
    // A pasted issuer URL: keep its tenant, or fall through to the full-URL
    // field when it is not the public-cloud shape.
    form.setIssuerUrl(value.trim());
    if (tenantIssuer.fromIssuer(value) === null) setFullIssuerChosen(true);
  };

  const useTenantField = (): void => {
    setFullIssuerChosen(false);
    if (storedTenant === null) form.setIssuerUrl('');
  };

  return (
    <div className="space-y-4" data-testid="idp-config">
      {showTenant && tenantIssuer ? (
        <div className="space-y-2">
          <Label htmlFor="idp-tenant">{tenantIssuer.label}</Label>
          <Input
            id="idp-tenant"
            value={storedTenant ?? ''}
            placeholder={tenantIssuer.placeholder}
            onChange={(e) => onTenantChange(e.target.value)}
            className="h-11 md:h-9"
            data-testid="idp-tenant"
          />
          <p className="text-xs text-muted-foreground">
            {tenantIssuer.help}{' '}
            {form.issuerUrl && (
              <>
                Issuer:{' '}
                <span className="font-mono break-all" data-testid="idp-derived-issuer">
                  {form.issuerUrl}
                </span>
                .{' '}
              </>
            )}
            <button
              type="button"
              onClick={() => setFullIssuerChosen(true)}
              className="underline underline-offset-2 hover:text-foreground"
              data-testid="idp-use-full-issuer"
            >
              Enter a full issuer URL instead
            </button>
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <Label htmlFor="idp-issuer">Issuer URL</Label>
          <Input
            id="idp-issuer"
            value={form.issuerUrl}
            placeholder={def.issuerPlaceholder}
            onChange={(e) => form.setIssuerUrl(e.target.value)}
            className="h-11 md:h-9"
            data-testid="idp-issuer"
          />
          <p className="text-xs text-muted-foreground">
            {def.issuerHelp}
            {tenantIssuer && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={useTenantField}
                  className="underline underline-offset-2 hover:text-foreground"
                  data-testid="idp-use-tenant"
                >
                  Enter the {tenantIssuer.label} instead
                </button>
              </>
            )}
          </p>
        </div>
      )}

      {form.showDisplayName && (
        <div className="space-y-2">
          <Label htmlFor="idp-display-name">
            Sign-in button label <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="idp-display-name"
            value={form.displayName}
            placeholder="Acme SSO"
            maxLength={64}
            onChange={(e) => form.setDisplayName(e.target.value)}
            className="h-11 md:h-9"
            data-testid="idp-display-name"
          />
          <p className="text-xs text-muted-foreground">
            Users see “Sign in with {form.displayName.trim() || 'SSO'}” on the login page.
          </p>
        </div>
      )}

      {form.isGoogle && (
        <GoogleClientFileImport
          redirectUri={redirectUri}
          onImport={({ clientId, clientSecret }) => {
            form.setClientId(clientId);
            form.setClientSecret(clientSecret);
          }}
        />
      )}

      <div className="space-y-2">
        <Label htmlFor="idp-client-id">Client ID</Label>
        <Input
          id="idp-client-id"
          value={form.clientId}
          onChange={(e) => form.setClientId(e.target.value)}
          className="h-11 md:h-9"
          data-testid="idp-client-id"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="idp-client-secret">
          Client secret{' '}
          {!form.secretRequired && <span className="text-muted-foreground">(optional)</span>}
        </Label>
        <Input
          id="idp-client-secret"
          type="password"
          value={form.clientSecret}
          onChange={(e) => form.setClientSecret(e.target.value)}
          className="h-11 md:h-9"
          data-testid="idp-client-secret"
        />
        <p className="text-xs text-muted-foreground">
          {secretHint ??
            (form.secretRequired
              ? 'Stored in Key Vault on save; never displayed again.'
              : 'Leave blank to keep the current secret. Enter a new value to rotate it.')}
        </p>
      </div>

      {form.showHostedDomains && (
        <div className="space-y-2">
          <Label htmlFor="idp-hosted-domains">
            Workspace domains <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="idp-hosted-domains"
            value={form.hostedDomains}
            placeholder="acme.com, acme.co.uk"
            onChange={(e) => form.setHostedDomains(e.target.value)}
            className="h-11 md:h-9"
            data-testid="idp-hosted-domains"
          />
          <p className="text-xs text-muted-foreground">
            Restricts sign-in to accounts in these Workspace domains. Google reports each person's
            own domain, not your primary one, so list every domain your Workspace uses. Leave blank
            if your OAuth client's consent screen is set to{' '}
            <span className="font-medium text-foreground">Internal</span> — Google already limits
            that client to your Workspace. With an External client, blank lets any Google account
            sign in.
          </p>
          {hostedDomainsExtra}
        </div>
      )}

      {form.isGoogle && (
        <p className="text-sm text-muted-foreground" data-testid="idp-google-roles-note">
          {googleRolesNote}
        </p>
      )}

      {/* Role source (Entra/Okta only). Google is always Greenlight-managed
          — no choice is offered (docs/10 §Google Workspace roles). */}
      {form.showRoleSource && (
        <fieldset className="space-y-2" data-testid="idp-role-source">
          <legend className="text-sm font-medium">Where do platform roles come from?</legend>
          <div className="grid gap-3 sm:grid-cols-2">
            <RoleSourceCard
              id="idp"
              selected={form.roleSource === 'idp'}
              title="IdP-managed"
              blurb="Map IdP groups to roles; roles are recomputed at every sign-in."
              onSelect={() => form.setRoleSource('idp')}
            />
            <RoleSourceCard
              id="greenlight"
              selected={form.roleSource === 'greenlight'}
              title="Greenlight-managed"
              blurb={greenlightBlurb}
              onSelect={() => form.setRoleSource('greenlight')}
            />
          </div>
          {def.roleSourceNote && (
            <p className="text-xs text-muted-foreground" data-testid="idp-oidc-role-note">
              {def.roleSourceNote}
            </p>
          )}
          {roleSourceExtra}
        </fieldset>
      )}

      {form.effectiveRoleSource === 'greenlight' && greenlightExtra}

      {/* Group→role mapping (Entra/Okta, IdP-managed only). Greenlight-managed
          assigns roles in-product; Google carries no group claim. */}
      {form.showGroupMapping && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="idp-group-claim">Group claim</Label>
            <Input
              id="idp-group-claim"
              value={form.groupClaim}
              onChange={(e) => form.setGroupClaim(e.target.value)}
              className="h-11 md:h-9"
              data-testid="idp-group-claim"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="idp-admin-group">Org admin group</Label>
            <Input
              id="idp-admin-group"
              value={form.adminGroupId}
              onChange={(e) => form.setAdminGroupId(e.target.value)}
              className="h-11 md:h-9"
              data-testid="idp-admin-group"
            />
            <p className="text-xs text-muted-foreground">{def.adminGroupHelp}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="idp-it-admin-group">
              Admin group <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="idp-it-admin-group"
              value={form.itAdminGroupId}
              onChange={(e) => form.setItAdminGroupId(e.target.value)}
              className="h-11 md:h-9"
              data-testid="idp-it-admin-group"
            />
            <p className="text-xs text-muted-foreground">
              Members get the governance admin role at sign-in.
            </p>
          </div>
        </div>
      )}

      {/* User sign-in restriction (`role_groups.user`). Orthogonal to role
          source (docs/10), so it stays editable in BOTH modes and is preserved
          across a switch. Hidden only for Google, whose only sign-in
          restriction is the hosted domain. */}
      {!form.isGoogle && (
        <div className="space-y-2">
          <Label htmlFor="idp-user-group">
            User sign-in group <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="idp-user-group"
            value={form.userGroupId}
            onChange={(e) => form.setUserGroupId(e.target.value)}
            className="h-11 md:h-9"
            data-testid="idp-user-group"
          />
          <p className="text-xs text-muted-foreground">{def.userGroupHelp}</p>
        </div>
      )}

      {/* Entering a group identifier only tells Greenlight which group to check
          at sign-in — it does not add anyone to that group in the IdP. Shown for
          IdP-managed Entra/Okta only (not Greenlight-managed or Google). */}
      {form.showGroupMapping && (
        <Alert data-testid="idp-group-membership-reminder">
          <AlertDescription>
            Add the actual users to these groups in {def.label} — entering a group ID here only
            tells Greenlight which group to check; it does not assign anyone to it.
            {form.userGroupId.trim() !== '' && (
              <>
                {' '}
                Only members of the user sign-in group above will be able to sign in once this is
                saved.
              </>
            )}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

function RoleSourceCard({
  id,
  selected,
  title,
  blurb,
  onSelect,
}: {
  id: RoleSource;
  selected: boolean;
  title: string;
  blurb: string;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      data-testid={`idp-role-source-${id}`}
      className={cn(
        'rounded-lg border p-3 text-left transition-colors min-h-11',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected ? 'border-primary ring-1 ring-primary' : 'hover:border-primary/60',
      )}
    >
      <span className="block font-medium">{title}</span>
      <span className="block text-sm text-muted-foreground">{blurb}</span>
    </button>
  );
}
